import { getUnixTime } from "date-fns";
import React, {
  createContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { API_URL, API_HEADERS } from "./globals";
import {
  bookingFormStepOneSchema,
  bookingFormStepTwoSchema,
} from "./formSchemas";

const FormContext = createContext();

const initialFormState = {
  bookings: [],
  rentalObject: "",
  rentType: "",
  attachmentIds: [],
  location: "",
  address: "",
  dates: [],
  customerName: "",
  customerCompany: "",
  customerPhone: "",
  customerEmail: "",
  customerAddress: "",
  remarks: "",
  termsAndConditions: false,
};

const FormProvider = ({ children }) => {
  const [errors, setErrors] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchError, setIsFetchError] = useState(false);
  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `${API_URL}api/booking?machine=${formData.rentalObject}`,
          {
            headers: { "X-API-Key": API_HEADERS["X-API-Key"] },
          },
        );
        const bookings = await response.json();
        setFormData((prevState) => ({
          ...prevState,
          bookings,
        }));
      } catch (error) {
        setIsFetchError(true);
      }
    };

    if (formData.rentalObject) {
      fetchData();
    }
  }, [formData.rentalObject]);

  useEffect(() => {
    setFormData((prevState) => ({
      ...initialFormState,
      rentType: prevState.rentType,
      rentalObject: prevState.rentalObject,
      bookings: prevState.bookings,
    }));
  }, [formData.rentType]);

  const resetForm = useCallback(() => {
    setErrors(null);
    setActiveStep(0);
    setFormData(() => ({ ...initialFormState, dates: [] }));
    setIsFetchError(false);
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const fullFormSchema = bookingFormStepOneSchema.concat(
        bookingFormStepTwoSchema,
      );

      if (activeStep === 0) {
        const isStepOneValid = await bookingFormStepOneSchema.isValid(
          formData,
          {
            abortEarly: false,
          },
        );

        if (isStepOneValid) {
          setActiveStep(activeStep + 1);
          setErrors(null);
        } else {
          bookingFormStepOneSchema
            .validate(formData, { abortEarly: false })
            .catch((err) => {
              const validationErrors = err.inner.reduce(
                (acc, validationError) => ({
                  ...acc,
                  [validationError.path]: validationError.errors[0],
                }),
                {},
              );
              setErrors(validationErrors);
            });
        }
      }

      if (activeStep === 1) {
        const isWholeFormValid = await fullFormSchema.isValid(formData, {
          abortEarly: false,
        });

        if (isWholeFormValid) {
          setIsLoading(true);
          setIsFetchError(false);

          const unixDates = formData.dates.map((date) => getUnixTime(date));

          const {
            rentalObject,
            rentType,
            attachmentIds,
            location,
            address,
            customerName,
            customerCompany,
            customerPhone,
            customerEmail,
            customerAddress,
            remarks,
          } = formData;

          fetch(`${API_URL}api/booking/addBooking`, {
            method: "POST",
            headers: {
              Accept: "application/json",
              ...API_HEADERS,
            },
            body: JSON.stringify({
              rentalObject,
              rentType,
              attachmentIds,
              location,
              address,
              customerName,
              customerCompany,
              customerPhone,
              customerEmail,
              customerAddress,
              remarks,
              dates: unixDates,
            }),
          })
            .then((resp) => {
              if (!resp.ok) {
                throw new Error(`Server error: ${resp.status}`);
              }
              return resp.json();
            })
            .then(() => {
              setActiveStep(activeStep + 1);
              setErrors(null);
            })
            .catch(() => {
              setIsFetchError(true);
            })
            .finally(() => {
              setIsLoading(false);
            });
        } else {
          fullFormSchema
            .validate(formData, { abortEarly: false })
            .catch((err) => {
              const validationErrors = err.inner.reduce(
                (acc, validationError) => ({
                  ...acc,
                  [validationError.path]: true,
                }),
                {},
              );
              setErrors(validationErrors);
            });
        }
      }
    },
    [activeStep, formData],
  );

  const value = useMemo(
    () => ({
      formData,
      activeStep,
      setFormData,
      setActiveStep,
      errors,
      handleSubmit,
      resetForm,
      isLoading,
      isFetchError,
    }),
    [
      formData,
      activeStep,
      errors,
      handleSubmit,
      resetForm,
      isLoading,
      isFetchError,
    ],
  );

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
};

export { FormContext, FormProvider };
